import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', form);
      login(data.access_token, data.user);
      toast.success('Login successful!');
      if (data.user.role === 'broker') navigate('/broker');
      else if (data.user.role === 'admin' || data.user.role === 'super_admin') navigate('/admin');
      else navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img src="https://images.unsplash.com/photo-1649989184479-73ab3b750a73?crop=entropy&cs=srgb&fm=jpg&q=85" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F2C59]/95 to-[#0F2C59]/70" />
        <div className="relative z-10 flex flex-col justify-center px-16 text-white">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="w-11 h-11 rounded-md bg-[#F97316] flex items-center justify-center text-white font-black text-xl">P</div>
            <span className="font-display font-extrabold text-2xl">PropertyByKilo</span>
          </Link>
          <h1 className="font-display font-extrabold text-4xl leading-tight tracking-tight">Welcome back to India's<br />leading PropTech platform.</h1>
          <p className="mt-4 text-slate-200 max-w-md">Manage your projects, capture leads, and grow your real estate business.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-md bg-[#0F2C59] flex items-center justify-center text-white font-black">P</div>
            <span className="font-display font-extrabold text-lg text-[#0F2C59]">PropertyByKilo</span>
          </Link>

          <h2 className="font-display font-bold text-3xl text-slate-900" data-testid="login-title">Sign In</h2>
          <p className="text-slate-500 text-sm mt-1">Access your dashboard</p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="mt-1 h-11"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Password</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                className="mt-1 h-11"
                data-testid="login-password-input"
              />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 bg-[#0F2C59] hover:bg-[#0F2C59]/90 font-semibold" data-testid="login-submit-btn">
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 p-4 bg-slate-100 rounded-md text-xs text-slate-600">
            <div className="font-semibold mb-1">Demo Admin Credentials:</div>
            <div>Email: admin@propertybykilo.com</div>
            <div>Password: Admin@123</div>
          </div>

          <p className="text-sm text-slate-500 mt-6 text-center">
            New to PropertyByKilo? <Link to="/register" className="font-semibold text-[#F97316]" data-testid="signup-link">Create account</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
